import React, { useContext, useEffect, useState } from "react";
import { FlexBox, SkeletonText, SkeletonTextLine } from "@vp/swan";
import { usePricingContext } from "../../../root/contexts/PricingContext";
import { useAppState } from "../../../root/contexts/AppContext";
import {
  useBffData,
  useProductIntermediary,
  useQuotePrice,
  useStartingAtPrice,
} from "../../../../hooks/queries";
import useTenantConfig from "../../../../hooks/useTenantConfig";
import useDebounce from "../../../../hooks/useDebounce";
import { GroupsAndTeamsContext } from "../../context/GroupsAndTeamsContext";
import { BaseGroupsAndTeamsMember } from "../../../../types/GroupsAndTeams";
import { useQuantity } from "../../../root/contexts/QuantityContext";
import { PricePresentation, PricePresentationType } from "@vp/price-platform";
import { buildPricesForPricePresentation } from "../../../../utils/WebsitePricingHelpers";
import { MoqFooterMessage } from "./MoqFooterMessage";
import { SetupFee } from "./SetupFee";

export const PricingSummary: React.FC<{}> = () => {
  const [isPriceChangeLoading, setIsPriceChangeLoading] = useState(false);
  const { culture } = useAppState();
  const { tenantConfig } = useTenantConfig();
  const { data: bffData, isFetched: bffDataIsFetched } = useBffData();
  const { minimumOrderQuantity } = bffData!;
  const { isFetched: quantityIsFetched } = useProductIntermediary();
  const { isPricingContextInitialized, pricingContextModule } =
    usePricingContext();
  const { validGroupsAndTeamsMembersForPricing } = useContext(
    GroupsAndTeamsContext,
  );
  const { totalQuantity } = useQuantity();
  const debouncedTotalQuantity = useDebounce(totalQuantity, 500);
  const debouncedGroupsAndTeamsMembersForPricing = useDebounce(
    validGroupsAndTeamsMembersForPricing,
    500,
  );
  const {
    data: productQuotePrice,
    isLoading: isLoadingQuotePrice,
    isFetched: quotePriceIsFetched,
  } = useQuotePrice(
    debouncedGroupsAndTeamsMembersForPricing as BaseGroupsAndTeamsMember[],
  );
  const {
    data: startingAtPrice,
    isLoading: isLoadingStartingAtPrice,
    isFetched: startingAtPriceIsFetched,
  } = useStartingAtPrice();

  const isLoading =
    !tenantConfig ||
    !startingAtPrice ||
    !productQuotePrice ||
    isLoadingStartingAtPrice ||
    !isPricingContextInitialized ||
    !bffDataIsFetched ||
    !bffData ||
    !quantityIsFetched ||
    !quotePriceIsFetched ||
    !startingAtPriceIsFetched;

  useEffect(() => {
    if (
      isLoadingQuotePrice ||
      debouncedGroupsAndTeamsMembersForPricing !==
        validGroupsAndTeamsMembersForPricing ||
      debouncedTotalQuantity !== totalQuantity
    ) {
      setIsPriceChangeLoading(true);
    } else {
      setIsPriceChangeLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoadingQuotePrice,
    debouncedGroupsAndTeamsMembersForPricing,
    debouncedTotalQuantity,
    totalQuantity,
  ]);

  const priceSkeletonText = (
    <SkeletonText style={{ width: "200px" }}>
      <SkeletonTextLine fontSize="large"></SkeletonTextLine>
      <SkeletonTextLine></SkeletonTextLine>
      {minimumOrderQuantity > 1 && totalQuantity === 0 && (
        <SkeletonTextLine></SkeletonTextLine>
      )}
    </SkeletonText>
  );

  return (
    <FlexBox
      className="pricing-summary"
      aria-live="polite"
      flexDirection="column"
    >
      {!isLoading && !isPriceChangeLoading ? (
        debouncedTotalQuantity === 0 ? (
          <>
            <PricePresentation
              presentationType={PricePresentationType.QtyStartingAtPrice}
              prices={buildPricesForPricePresentation(startingAtPrice)}
              priceProperties={{
                minimumFractionDigits: startingAtPrice?.fractionDigits || 2,
                locale: culture,
                currency: startingAtPrice?.currency,
                vatInc: pricingContextModule.vatInclusive,
                quantity: minimumOrderQuantity,
                hideVatMessage: false,
              }}
            />
            {minimumOrderQuantity > 1 && (
              <MoqFooterMessage
                moq={minimumOrderQuantity}
                inPriceSummary={true}
              />
            )}
            <SetupFee />
          </>
        ) : (
          <>
            <PricePresentation
              presentationType={PricePresentationType.PriceForQtyUnits}
              prices={buildPricesForPricePresentation(productQuotePrice)}
              priceProperties={{
                minimumFractionDigits: productQuotePrice?.fractionDigits || 2,
                locale: culture,
                currency: productQuotePrice?.currency,
                vatInc: pricingContextModule.vatInclusive,
                quantity: totalQuantity,
                hideVatMessage: false,
              }}
            />
            <SetupFee />
          </>
        )
      ) : (
        priceSkeletonText
      )}
    </FlexBox>
  );
};
